import React from "react";
import {
  Box,
  Badge,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  SimpleGrid,
  Link,
  ListItem,
  UnorderedList,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FiRefreshCw, FiTrash2 } from "react-icons/fi";

/** @typedef {'teal' | 'purple'} AiTheme */

export function useAiFeatureColors(theme) {
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const panelBg = useColorModeValue("gray.50", "gray.900");
  const historyHover = useColorModeValue("gray.50", "gray.700");
  const historyActivePurple = useColorModeValue("purple.50", "purple.900");
  const historyActiveTeal = useColorModeValue("teal.50", "teal.900");
  const historyActive =
    theme === "purple" ? historyActivePurple : historyActiveTeal;
  const heroGradientPurple = useColorModeValue(
    "linear(to-br, purple.400, pink.500, violet.600)",
    "linear(to-br, purple.700, pink.800, violet.900)"
  );
  const heroGradientTeal = useColorModeValue(
    "linear(to-br, teal.400, cyan.500, blue.600)",
    "linear(to-br, teal.600, cyan.700, blue.900)"
  );
  const heroGradient =
    theme === "purple" ? heroGradientPurple : heroGradientTeal;
  const accent = theme === "purple" ? "purple" : "teal";
  const chipBgPurple = useColorModeValue("purple.50", "purple.900");
  const chipBgTeal = useColorModeValue("cyan.50", "cyan.900");
  const chipBg = theme === "purple" ? chipBgPurple : chipBgTeal;

  return {
    pageBg,
    cardBg,
    borderColor,
    muted,
    panelBg,
    historyHover,
    historyActive,
    heroGradient,
    accent,
    chipBg,
  };
}

/**
 * Readable intro panel (light + dark) — matches Dashboard tab guide styling.
 * @param {{ title: string, intro: string, helps: string[], theme?: 'teal' | 'purple', children?: React.ReactNode, showWorkspaceLink?: boolean }} props
 */
export function AiFeatureGuidePanel({
  title,
  intro,
  helps = [],
  theme = "teal",
  children,
  showWorkspaceLink = true,
}) {
  const shellBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue(
    theme === "purple" ? "purple.100" : "teal.100",
    theme === "purple" ? "purple.700" : "teal.700"
  );
  const muted = useColorModeValue("gray.600", "gray.400");
  const headingCol = useColorModeValue("gray.800", "white");
  const linkCol = useColorModeValue(
    theme === "purple" ? "purple.600" : "teal.600",
    theme === "purple" ? "purple.300" : "teal.300"
  );

  return (
    <Box
      borderWidth="1px"
      borderColor={border}
      borderRadius="2xl"
      bg={shellBg}
      px={{ base: 4, md: 5 }}
      py={{ base: 4, md: 5 }}
      boxShadow="md"
      mb={5}
    >
      <Heading as="h2" size="sm" fontWeight="bold" color={headingCol} mb={1}>
        {title} — what this tab is for
      </Heading>
      <Text fontSize="sm" color={muted} mb={2} lineHeight="tall">
        {intro}
      </Text>
      {helps.length > 0 ? (
        <UnorderedList
          fontSize="sm"
          color={muted}
          spacing={1}
          pl={4}
          mb={3}
          styleType="disc"
        >
          {helps.map((line) => (
            <ListItem key={line} lineHeight="tall">
              {line}
            </ListItem>
          ))}
        </UnorderedList>
      ) : null}
      {children}
      {showWorkspaceLink ? (
        <Text fontSize="xs" color={muted} mt={children ? 3 : 0}>
          <Link
            as={RouterLink}
            to="/dashboard"
            color={linkCol}
            fontWeight="semibold"
            _hover={{ textDecoration: "underline" }}
          >
            Workspace home
          </Link>
          {" · "}
          Use the left menu to open other tools in Manage360.
        </Text>
      ) : null}
    </Box>
  );
}

export function AiCreditsBanner({
  canUse,
  remaining,
  onBuy,
  variant = "light",
}) {
  const heroBadgeBg = useColorModeValue("blackAlpha.600", "whiteAlpha.300");
  const heroBadgeColor = "white";

  if (canUse) {
    if (variant === "hero") {
      return (
        <Badge
          bg={heroBadgeBg}
          color={heroBadgeColor}
          mb={4}
          px={3}
          py={1}
          borderRadius="full"
          fontSize="sm"
          fontWeight="medium"
          boxShadow="sm"
        >
          {remaining.toLocaleString()} AI Usage Credits available
        </Badge>
      );
    }
    return (
      <Badge colorScheme="teal" mb={4} px={3} py={1} borderRadius="full">
        {remaining.toLocaleString()} AI Usage Credits available
      </Badge>
    );
  }

  return (
    <Alert
      status="warning"
      variant={variant === "hero" ? "solid" : "subtle"}
      borderRadius="xl"
      mb={4}
      flexDirection={{ base: "column", sm: "row" }}
      alignItems={{ base: "stretch", sm: "center" }}
    >
      <AlertIcon />
      <Box flex="1">
        <AlertTitle fontSize="sm">AI Usage Credits required</AlertTitle>
        <AlertDescription fontSize="sm">
          Top up from Profile to run new analysis. You can still open past
          results in history.
        </AlertDescription>
      </Box>
      {onBuy ? (
        <Button size="sm" colorScheme="yellow" onClick={onBuy} flexShrink={0}>
          Buy credits or plan
        </Button>
      ) : null}
    </Alert>
  );
}

export function AiHistorySidebar({
  theme,
  title = "History",
  newLabel = "New",
  isNewActive,
  onNew,
  onRefresh,
  loading,
  emptyText,
  children,
  canUseNew = true,
  onBuyCredits,
}) {
  const { cardBg, borderColor, muted, accent } = useAiFeatureColors(theme);

  const handleNew = () => {
    if (!canUseNew && onBuyCredits) {
      onBuyCredits();
      return;
    }
    onNew?.();
  };

  return (
    <Box
      w={{ base: "100%", lg: "300px" }}
      flexShrink={0}
      bg={cardBg}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="lg"
      overflow="hidden"
      maxH={{ lg: "calc(100vh - 6rem)" }}
      display="flex"
      flexDirection="column"
    >
      <Box
        p={4}
        borderBottomWidth="1px"
        borderColor={borderColor}
        bgGradient={
          theme === "purple"
            ? "linear(to-r, purple.500, pink.500)"
            : "linear(to-r, teal.500, cyan.500)"
        }
        color="white"
      >
        <HStack justify="space-between" mb={3}>
          <Heading size="sm">{title}</Heading>
          <IconButton
            size="sm"
            variant="ghost"
            color="white"
            aria-label="Refresh"
            icon={<FiRefreshCw />}
            onClick={onRefresh}
            _hover={{ bg: "whiteAlpha.200" }}
          />
        </HStack>
        <Button
          size="sm"
          w="full"
          variant={isNewActive ? "solid" : "outline"}
          bg={isNewActive ? "white" : "transparent"}
          color={isNewActive ? `${accent}.700` : "white"}
          borderColor="whiteAlpha.500"
          _hover={{ bg: "whiteAlpha.200" }}
          onClick={handleNew}
        >
          {newLabel}
        </Button>
      </Box>
      <Box flex="1" overflowY="auto" p={2}>
        {loading ? (
          <Flex justify="center" py={10}>
            <Spinner color={`${accent}.500`} />
          </Flex>
        ) : !children ||
          (Array.isArray(children) && children.length === 0) ? (
          <Text fontSize="sm" color={muted} p={4} textAlign="center" lineHeight="tall">
            {emptyText}
          </Text>
        ) : (
          <VStack align="stretch" spacing={1}>
            {children}
          </VStack>
        )}
      </Box>
    </Box>
  );
}

export function AiHistoryItem({
  theme,
  active,
  title,
  meta,
  preview,
  onSelect,
  onDelete,
  statusBadge,
}) {
  const { historyHover, historyActive, muted, accent } =
    useAiFeatureColors(theme);

  return (
    <Box
      p={3}
      borderRadius="xl"
      cursor="pointer"
      bg={active ? historyActive : "transparent"}
      _hover={{ bg: active ? historyActive : historyHover }}
      borderWidth="1px"
      borderColor={active ? `${accent}.300` : "transparent"}
      onClick={onSelect}
      transition="all 0.15s"
    >
      <HStack justify="space-between" align="start">
        <Box flex="1" minW={0}>
          <HStack spacing={2} mb={1}>
            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
              {title}
            </Text>
            {statusBadge}
          </HStack>
          {meta}
          {preview ? (
            <Text fontSize="xs" color={muted} noOfLines={2} mt={2} lineHeight="short">
              {preview}
            </Text>
          ) : null}
        </Box>
        <IconButton
          size="xs"
          variant="ghost"
          colorScheme="red"
          aria-label="Delete"
          icon={<FiTrash2 />}
          onClick={onDelete}
        />
      </HStack>
    </Box>
  );
}

/**
 * Blocks capture/upload actions when out of credits; history remains usable underneath.
 */
export function AiCreditsPaywall({
  show,
  featureName,
  onBuy,
  theme = "teal",
}) {
  const { cardBg, accent, heroGradient, muted } = useAiFeatureColors(theme);
  const paywallSubtext = useColorModeValue("gray.500", "gray.400");
  if (!show) return null;

  return (
    <Box
      position="absolute"
      inset={0}
      zIndex={20}
      borderRadius="2xl"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={6}
    >
      <Box
        position="absolute"
        inset={0}
        bg="blackAlpha.700"
        backdropFilter="blur(6px)"
      />
      <Box
        position="relative"
        maxW="md"
        w="full"
        bg={cardBg}
        borderRadius="2xl"
        p={{ base: 6, md: 8 }}
        boxShadow="2xl"
        textAlign="center"
        borderWidth="2px"
        borderColor={`${accent}.300`}
      >
        <Box
          h="3px"
          w="full"
          bgGradient={heroGradient}
          position="absolute"
          top={0}
          left={0}
          right={0}
          borderTopRadius="2xl"
        />
        <Badge colorScheme={accent} mb={3} px={3} py={1} borderRadius="full">
          AI Usage Credits required
        </Badge>
        <Heading size="md" mb={2}>
          Top up to use {featureName}
        </Heading>
        <Text fontSize="sm" color={muted} mb={5} lineHeight="tall">
          You do not have enough AI Usage Credits. Choose a credit pack or plan
          to continue — payment is handled securely via Razorpay.
        </Text>
        <Button
          size="lg"
          colorScheme={accent}
          w="full"
          onClick={onBuy}
          mb={2}
        >
          Buy credits or plan
        </Button>
        <Text fontSize="xs" color={paywallSubtext}>
          Past sessions in history stay available without new credits.
        </Text>
      </Box>
    </Box>
  );
}

export function AiHeroCard({ theme, title, subtitle, children, recording }) {
  const { heroGradient } = useAiFeatureColors(theme);
  const heroTextShadow = useColorModeValue(
    "0 1px 3px rgba(0,0,0,0.35)",
    "none"
  );
  return (
    <Box
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="xl"
      bgGradient={heroGradient}
      p={{ base: 5, md: 8 }}
      position="relative"
    >
      {recording ? (
        <Box
          position="absolute"
          inset={0}
          opacity={0.15}
          bg="white"
          sx={{
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 0.08 },
              "50%": { opacity: 0.2 },
            },
          }}
        />
      ) : null}
      <Box position="relative">
        <Heading
          size="lg"
          mb={1}
          color="white"
          textShadow={heroTextShadow}
        >
          {title}
        </Heading>
        {subtitle ? (
          <Text
            fontSize="sm"
            mb={3}
            maxW="2xl"
            lineHeight="tall"
            color="white"
            textShadow={heroTextShadow}
            opacity={0.95}
          >
            {subtitle}
          </Text>
        ) : null}
        <Box>{children}</Box>
      </Box>
    </Box>
  );
}

export function AiStepStrip({ steps, theme = "teal" }) {
  const { cardBg, borderColor, accent } = useAiFeatureColors(theme);
  const stepTitleColor = useColorModeValue("gray.800", "gray.100");
  const stepHintColor = useColorModeValue("gray.600", "gray.400");
  return (
    <SimpleGrid columns={{ base: 1, md: steps.length }} spacing={3} mb={5}>
      {steps.map((step, i) => (
        <Box
          key={step.label}
          p={3}
          borderRadius="xl"
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          color={stepTitleColor}
          boxShadow="sm"
        >
          <Badge colorScheme={accent} mb={2}>
            Step {i + 1}
          </Badge>
          <Text fontSize="sm" fontWeight="semibold" color={stepTitleColor}>
            {step.label}
          </Text>
          <Text fontSize="xs" color={stepHintColor} mt={1}>
            {step.hint}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  );
}
